import { type ReactNode, useState } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretRightIcon,
  DatabaseIcon,
  DotsSixVerticalIcon,
  DotsThreeIcon,
  GearSixIcon,
  HeartbeatIcon,
  HouseIcon,
  LockIcon,
  PaintBrushIcon,
  PlusIcon,
} from '@phosphor-icons/react';

import { buildPageTree } from '@/lib/page-tree';
import { usePublicSiteUrl } from '@/hooks/use-public-site-url';
import type { Page } from '@/types/database';

interface PagesPanelProps {
  pages: Page[];
  selectedPageId: string | null;
  canManage: boolean;
  onSelectPage: (pageId: string) => void;
  onCreatePage: (title: string, parentPageId: string | null, showInNav: boolean) => void;
  onOpenPageSettings: (pageId: string) => void;
  onReorderPage: (pageId: string, overPageId: string) => void;
  onMoveTopLevelPage: (pageId: string, showInNav: boolean, overPageId: string | null) => void;
  // Site-wide destinations (spec's Data/People/Settings/Health, plus Site
  // Styles) — kept out of StudioTopBar, which is deliberately minimal
  // (device/state only), and live here instead since the Pages panel is
  // already the site's persistent left nav. Style and Settings both write
  // site-level records rather than page content, so they gate on
  // website_settings:manage independent of canManage
  // (website_content:manage), same as Style did in StudioTopBar before moving.
  onOpenData: () => void;
  onOpenPeople: () => void;
  canManageSettings: boolean;
  onOpenSettings: () => void;
  onOpenHealth: () => void;
  canManageStyles: boolean;
  onOpenStyles: () => void;
}

const NAV_SECTION_ID = 'section:main-navigation';
const NOT_LINKED_SECTION_ID = 'section:not-linked';
type SectionId = typeof NAV_SECTION_ID | typeof NOT_LINKED_SECTION_ID;

export function PagesPanel({
  pages,
  selectedPageId,
  canManage,
  onSelectPage,
  onCreatePage,
  onOpenPageSettings,
  onReorderPage,
  onMoveTopLevelPage,
  onOpenData,
  onOpenPeople,
  canManageSettings,
  onOpenSettings,
  onOpenHealth,
  canManageStyles,
  onOpenStyles,
}: PagesPanelProps) {
  const { navTree, notLinked } = buildPageTree(pages);
  const [navOpen, setNavOpen] = useState(true);
  const [notLinkedOpen, setNotLinkedOpen] = useState(true);
  const { url: publicSiteUrl } = usePublicSiteUrl();

  // Top-level page ids only — L2 children stay nested under their L1
  // parent and reorder within that nested list (unaffected by this
  // cross-section drag); moving a child out of nav is done via its Page
  // Settings "Show in navigation" toggle instead.
  const navTopLevelIds = navTree.map((node) => node.page.id);
  const notLinkedTopLevelIds = notLinked.filter((page) => page.parent_page_id === null).map((page) => page.id);
  const notLinkedNestedRows = notLinked.filter((page) => page.parent_page_id !== null);

  // null outside of a drag — the displayed containers are then derived
  // directly from the pages prop each render (no effect needed to keep
  // them in sync). While dragging, this holds a local preview so dnd-kit
  // can show an item moving between sections live before the drop is
  // persisted; dragStart seeds it, dragEnd/dragCancel clears it back to
  // null so the next render derives fresh from (by then updated) props.
  const [dragContainers, setDragContainers] = useState<Record<SectionId, string[]> | null>(null);
  const containers: Record<SectionId, string[]> =
    dragContainers ?? { [NAV_SECTION_ID]: navTopLevelIds, [NOT_LINKED_SECTION_ID]: notLinkedTopLevelIds };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findContainer = (id: string): SectionId | undefined => {
    if (id === NAV_SECTION_ID || id === NOT_LINKED_SECTION_ID) return id;
    if (containers[NAV_SECTION_ID].includes(id)) return NAV_SECTION_ID;
    if (containers[NOT_LINKED_SECTION_ID].includes(id)) return NOT_LINKED_SECTION_ID;
    return undefined;
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeContainer = findContainer(active.id as string);
    const overContainer = findContainer(over.id as string);
    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    setDragContainers((prev) => {
      const current = prev ?? containers;
      const activeItems = current[activeContainer].filter((id) => id !== active.id);
      const overItems = [...current[overContainer]];
      const overIndex = overItems.indexOf(over.id as string);
      overItems.splice(overIndex >= 0 ? overIndex : overItems.length, 0, active.id as string);
      return { ...current, [activeContainer]: activeItems, [overContainer]: overItems };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !canManage) {
      setDragContainers(null);
      return;
    }

    const finalContainer = findContainer(active.id as string);
    const startedInContainer = navTopLevelIds.includes(active.id as string) ? NAV_SECTION_ID : NOT_LINKED_SECTION_ID;

    if (finalContainer) {
      const items = containers[finalContainer];
      const activeIndex = items.indexOf(active.id as string);
      const overIsItem = over.id !== NAV_SECTION_ID && over.id !== NOT_LINKED_SECTION_ID;
      const overPageId = overIsItem && over.id !== active.id ? (over.id as string) : (items[activeIndex + 1] ?? null);

      if (finalContainer !== startedInContainer) {
        onMoveTopLevelPage(active.id as string, finalContainer === NAV_SECTION_ID, overPageId);
      } else if (overPageId) {
        onReorderPage(active.id as string, overPageId);
      }
    }
    setDragContainers(null);
  };

  return (
    <Stack
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setDragContainers(containers)}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDragContainers(null)}
      >
        <Stack
          spacing={2}
          sx={{
            flex: 1,
            minHeight: 0,
            p: 2,
            overflowY: 'auto',
          }}
        >
          <Section
            title="Main Navigation"
            sectionId={NAV_SECTION_ID}
            open={navOpen}
            onToggleOpen={() => setNavOpen((value) => !value)}
            canManage={canManage}
            onAddPage={() => onCreatePage('Untitled Page', null, true)}
          >
            <SortableContext items={containers[NAV_SECTION_ID]} strategy={verticalListSortingStrategy}>
              <Stack spacing={0.25}>
                {containers[NAV_SECTION_ID].map((pageId) => {
                  const node = navTree.find((item) => item.page.id === pageId);
                  if (!node) return null;
                  return (
                    <Stack key={node.page.id} spacing={0.25}>
                      <PageRow
                        page={node.page}
                        depth={0}
                        selected={node.page.id === selectedPageId}
                        canManage={canManage}
                        onSelect={() => onSelectPage(node.page.id)}
                        onOpenSettings={() => onOpenPageSettings(node.page.id)}
                        onAddChild={() => onCreatePage('Untitled Page', node.page.id, true)}
                      />
                      {node.children.length > 0 ? (
                        <NestedChildrenList
                          childPages={node.children}
                          selectedPageId={selectedPageId}
                          canManage={canManage}
                          onSelectPage={onSelectPage}
                          onOpenPageSettings={onOpenPageSettings}
                          onReorderPage={onReorderPage}
                        />
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            </SortableContext>
          </Section>

          <Section
            title="Not Linked"
            sectionId={NOT_LINKED_SECTION_ID}
            open={notLinkedOpen}
            onToggleOpen={() => setNotLinkedOpen((value) => !value)}
            canManage={canManage}
            onAddPage={() => onCreatePage('Untitled Page', null, false)}
          >
            <SortableContext items={containers[NOT_LINKED_SECTION_ID]} strategy={verticalListSortingStrategy}>
              <Stack spacing={0.25}>
                {containers[NOT_LINKED_SECTION_ID].map((pageId) => {
                  const page = notLinked.find((item) => item.id === pageId);
                  if (!page) return null;
                  return (
                    <PageRow
                      key={page.id}
                      page={page}
                      depth={0}
                      selected={page.id === selectedPageId}
                      canManage={canManage}
                      onSelect={() => onSelectPage(page.id)}
                      onOpenSettings={() => onOpenPageSettings(page.id)}
                    />
                  );
                })}
                {/* Orphaned L2 children (parent no longer in nav) — not part of
                    the cross-section drag set since they're not top-level, but
                    still need to render somewhere reachable. */}
                {notLinkedNestedRows.map((page) => (
                  <PageRow
                    key={page.id}
                    page={page}
                    depth={0}
                    selected={page.id === selectedPageId}
                    canManage={canManage}
                    onSelect={() => onSelectPage(page.id)}
                    onOpenSettings={() => onOpenPageSettings(page.id)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </Section>
        </Stack>
      </DndContext>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          {canManageStyles ? (
            <Tooltip title="Site Styles">
              <IconButton onClick={onOpenStyles} aria-label="Site Styles">
                <PaintBrushIcon size={18} />
              </IconButton>
            </Tooltip>
          ) : null}
          {canManage ? (
            <Tooltip title="Data">
              <IconButton onClick={onOpenData} aria-label="Data">
                <DatabaseIcon size={18} />
              </IconButton>
            </Tooltip>
          ) : null}
          {canManage ? (
            <Tooltip title="Permissions Review">
              <IconButton onClick={onOpenPeople} aria-label="Permissions Review">
                <LockIcon size={18} />
              </IconButton>
            </Tooltip>
          ) : null}
          {canManageSettings ? (
            <Tooltip title="Settings">
              <IconButton onClick={onOpenSettings} aria-label="Settings">
                <GearSixIcon size={18} />
              </IconButton>
            </Tooltip>
          ) : null}
          {canManage ? (
            <Tooltip title="Health">
              <IconButton onClick={onOpenHealth} aria-label="Health">
                <HeartbeatIcon size={18} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={publicSiteUrl ? 'View public site' : 'Public site not available yet'}>
            <span>
              <IconButton
                component={publicSiteUrl ? 'a' : 'button'}
                href={publicSiteUrl ?? undefined}
                target={publicSiteUrl ? '_blank' : undefined}
                rel={publicSiteUrl ? 'noopener noreferrer' : undefined}
                disabled={!publicSiteUrl}
                aria-label="View public site"
              >
                <ArrowSquareOutIcon size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Stack>
  );
}

function NestedChildrenList({
  childPages,
  selectedPageId,
  canManage,
  onSelectPage,
  onOpenPageSettings,
  onReorderPage,
}: {
  childPages: Page[];
  selectedPageId: string | null;
  canManage: boolean;
  onSelectPage: (pageId: string) => void;
  onOpenPageSettings: (pageId: string) => void;
  onReorderPage: (pageId: string, overPageId: string) => void;
}) {
  // A separate, nested DndContext scoped to just this parent's own
  // children — reordering L2 pages among themselves only, independent of
  // the outer cross-section drag (each useSortable binds to its nearest
  // ancestor DndContext).
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) onReorderPage(active.id as string, over.id as string);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={childPages.map((child) => child.id)} strategy={verticalListSortingStrategy}>
        <Stack spacing={0.25}>
          {childPages.map((child) => (
            <PageRow
              key={child.id}
              page={child}
              depth={1}
              selected={child.id === selectedPageId}
              canManage={canManage}
              onSelect={() => onSelectPage(child.id)}
              onOpenSettings={() => onOpenPageSettings(child.id)}
            />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
}

function Section({
  title,
  sectionId,
  open,
  onToggleOpen,
  canManage,
  onAddPage,
  children,
}: {
  title: string;
  sectionId: SectionId;
  open: boolean;
  onToggleOpen: () => void;
  canManage: boolean;
  onAddPage: () => void;
  children: ReactNode;
}) {
  // Always a valid drop target, even with zero pages in it — without this,
  // an empty section has no droppable surface to drag a page into.
  const { setNodeRef, isOver } = useDroppable({ id: sectionId });

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', cursor: 'pointer' }} onClick={onToggleOpen}>
          <IconButton>{open ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}</IconButton>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>{title}</Typography>
        </Stack>
        {canManage ? (
          <Tooltip title="Add page">
            <IconButton onClick={onAddPage} aria-label={`Add page to ${title}`}>
              <PlusIcon size={16} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      {open ? (
        <Box
          ref={setNodeRef}
          sx={{
            minHeight: 32,
            borderRadius: 1,
            outline: isOver ? '2px dashed' : 'none',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          }}
        >
          {children}
        </Box>
      ) : null}
    </Stack>
  );
}

interface PageRowProps {
  page: Page;
  depth: number;
  selected: boolean;
  canManage: boolean;
  onSelect: () => void;
  onOpenSettings: () => void;
  onAddChild?: () => void;
}

function PageRow({ page, depth, selected, canManage, onSelect, onOpenSettings, onAddChild }: PageRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
    disabled: !canManage,
  });

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      spacing={0.25}
      sx={{
        alignItems: 'center',
        pl: depth * 2.5,
        py: 0.25,
        pr: 0.5,
        borderRadius: 1,
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      {canManage ? (
        <Box
          component="button"
          type="button"
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            touchAction: 'none',
            border: 'none',
            bgcolor: 'transparent',
            display: 'inline-flex',
            color: 'text.disabled',
            p: 0.25,
          }}
          aria-label={`Drag to reorder ${page.title}`}
        >
          <DotsSixVerticalIcon size={16} />
        </Box>
      ) : null}

      {page.is_home ? (
        <Tooltip title="Home page">
          <HouseIcon size={14} weight="fill" style={{ flexShrink: 0, opacity: 0.7 }} />
        </Tooltip>
      ) : null}

      <Typography
        variant="body2"
        onClick={onSelect}
        sx={{
          flex: 1,
          cursor: 'pointer',
          fontWeight: selected ? 700 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {page.title}
      </Typography>

      <Chip
        label={page.is_published ? 'Published' : 'Draft'}
        size="small"
        color={page.is_published ? 'success' : 'default'}
        variant={page.is_published ? 'filled' : 'outlined'}
        sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
      />

      {canManage ? (
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          {onAddChild ? (
            <Tooltip title="Add sub-page">
              <IconButton onClick={onAddChild} aria-label={`Add sub-page under ${page.title}`}>
                <PlusIcon size={16} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Page settings">
            <IconButton onClick={onOpenSettings} aria-label={`Settings for ${page.title}`}>
              <DotsThreeIcon size={20} weight="bold" />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
    </Stack>
  );
}
